import { BriefDeliverableTaskUploadTypeEnum } from "@entities/brief/lib/constants";
import { useUploadTaskContent } from "@entities/campaigns/lib/hooks/useUploadTaskContent";
import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";
import { AddBriefCampaignLive } from "@features/BriefCampaignLive/AddBriefCampaignLive";
import { ToastService } from "@shared/lib/services";
import { ICampaignDeliverableTaskForm } from "@shared/lib/types/briefCampains";
import { Button, Modal } from "@shared/ui";
import dayjs from "dayjs";
import { CheckCircle2, Clock, ExternalLink, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import styles from "./AddCampaignLive.module.scss";

interface Props {
  taskId: string;
  campaignInfluencerId: string;
  deliverableConfigRefId: string;
  taskForm?: ICampaignDeliverableTaskForm;
  refetchInfluencer: () => void;
  briefId: string;
}

export default function AddCampaignLive({
  taskForm,
  campaignInfluencerId,
  deliverableConfigRefId,
  taskId,
  refetchInfluencer,
  briefId,
}: Props) {
  const { uploadTaskContent, isUploading, isUploaded } = useUploadTaskContent(
    briefId,
    refetchInfluencer
  );

  const [link, setLink] = useState("");
  const [error, setError] = useState<string>("");
  const { userDetails } = useGetMe();
  const [isLiveVisible, setisLiveVisible] = useState<boolean>(false);
  // const isAddLiveDisabled =
  //   userDetails?.userEntity?.permissions.includes("super-brand-org") ||
  //   userDetails?.userEntity?.permissions.includes("super")
  //     ? false
  //     : !userDetails?.userEntity?.permissions.includes(
  //         "approve-tasks-brand-org"
  //       );

  // Check if contentUrls exist and have items
  const hasContentUrls = useMemo(() => {
    return !!(
      taskForm?.contentUrls &&
      Array.isArray(taskForm.contentUrls) &&
      taskForm.contentUrls.length > 0
    );
  }, [taskForm?.contentUrls]);

  // Get the live link from contentUrls
  const liveLink = useMemo(() => {
    if (!hasContentUrls) return "";
    const mediaLink = taskForm?.contentUrls?.find(
      (item) => item.type === BriefDeliverableTaskUploadTypeEnum.MediaLink
    );
    return mediaLink?.url || "";
  }, [hasContentUrls, taskForm?.contentUrls]);

  // Format live date
  const liveDate = useMemo(() => {
    if (!hasContentUrls) return "";

    if (!taskForm?.reportMedia) return "";

    const reportMedia = taskForm?.reportMedia[0];

    if (!reportMedia) return "";

    return dayjs(reportMedia.postedOn).format("MMM DD");
  }, [taskForm?.reportMedia]);

  useEffect(() => {
    if (isUploaded) {
      ToastService.success("Live link submitted");
      setLink("");
      setError("");
    }
  }, [isUploaded]);

  // Clear error when user types
  const handleLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLink(e.target.value);
    if (error) {
      setError("");
    }
  };

  const validateUrl = (url: string): boolean => {
    try {
      const trimmedUrl = url.trim();
      if (!trimmedUrl) return false;

      // Basic URL validation
      const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
      if (!urlPattern.test(trimmedUrl)) {
        return false;
      }

      // Try to create a URL object to validate
      new URL(trimmedUrl);
      return true;
    } catch {
      return false;
    }
  };

  const handleCreateLive = () => {
    setisLiveVisible(true);
  };

  const handleSave = async () => {
    const trimmedLink = link.trim();

    // Clear previous error
    setError("");

    if (!trimmedLink) {
      setError("Please enter a live link");
      return;
    }

    if (!validateUrl(trimmedLink)) {
      setError(
        "Please enter a valid URL (e.g., https://instagram.com/reel/...)"
      );
      return;
    }

    try {
      const payload = {
        campaignInfluencerId,
        deliverableConfigRefId,
        taskId,
        contentUrls: [
          {
            url: trimmedLink,
            type: BriefDeliverableTaskUploadTypeEnum.MediaLink,
          },
        ],
      };

      uploadTaskContent({ data: payload });
    } catch (e) {
      ToastService.error("Failed to submit live link. Please try again.");
    }
  };

  // Pending state - no contentUrls
  if (!hasContentUrls) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>
          <Clock size={18} />
        </div>
        <p className={styles.emptyTextTitle}>Live pending</p>
        <p className={styles.emptyTextDescription}>
          Live is still pending from the creator. You will be able to review it
          once it is submitted.
        </p>
        {/* <DisabledPopover isDisabled={isAddLiveDisabled}> */}
        <Button
          // disabled={isAddLiveDisabled}
          type="primary"
          icon={<Plus size={14} />}
          onClick={handleCreateLive}
          className={styles.createButton}
        >
          Create Live
        </Button>
        {/* </DisabledPopover> */}

        <Modal
          open={isLiveVisible}
          onCancel={() => setisLiveVisible(false)}
          footer={null}
          width={1200}
          title=""
          destroyOnClose
        >
          <AddBriefCampaignLive
            taskId={taskId}
            campaignInfluencerId={campaignInfluencerId}
            deliverableConfigRefId={deliverableConfigRefId}
            onClose={() => setisLiveVisible(false)}
          />
        </Modal>
      </div>
    );
  }

  // Completed state - contentUrls exist
  return (
    <div className={styles.addCampaignLive}>
      <div className={styles.completedState}>
        <div className={styles.successMessage}>
          <CheckCircle2 size={16} />
          <span className={styles.successText}>Content has gone Live!</span>
        </div>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            Live Date: <span className={styles.infoValue}>{liveDate}</span>
          </div>
          <div className={styles.infoItem}>
            Link:{" "}
            <a
              href={liveLink || "#"}
              target="_blank"
              rel="noreferrer"
              className={styles.link}
            >
              {liveLink || "https://instagram.com/reel/..."}
            </a>
          </div>
        </div>
        <div className={styles.actions}>
          <a
            href={liveLink || "#"}
            target="_blank"
            rel="noreferrer"
            className={styles.viewPostButton}
          >
            View Post <ExternalLink size={12} />
          </a>
        </div>
      </div>
    </div>
  );
}
